import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { TagContainer, TagLogo, TagTooltipIcon } from './tag.styles';
import { colorPalette } from '../../utils/theme';
import LogoIcon from '../../assets/icons/logo-icon-white.png';
import LogoIconDark from '../../assets/icons/logo-icon-dark.png';
import { MaterialSymbol } from '@material-symbols/font-400';
import MaterialIcon from '../material-icon';
import useIsMobile from '../../hooks/useIsMobile';

interface UnitStatusTagProps {
    label: string;
    size?: 'L' | 'S' | 'XS' | 'M';
    type: 'unitStatus';
    variant: 'inProgress' | 'handover' | 'vacant' | 'rented' | 'completed' | 'selfInterest' | 'canceled' | 'terminated' | 'reserved' | 'legal' | 'noManagement';
    icon?: MaterialSymbol;
    tooltip?: string;
    backgroundColor?: string;
    color?: string;
}

interface PackageTagProps {
    size?: 'L' | 'S' | 'XS' | 'M';
    type: 'package';
    variant: 'default' | 'lite' | 'basic' | 'premium' | 'premiumSpecial' | 'noManagement';
    icon?: MaterialSymbol;
    tooltip?: string;
    backgroundColor?: string;
    color?: string;
}

// Combine both prop types into a single type
type TagProps = UnitStatusTagProps | PackageTagProps;

// Define styles for unit status
// Rented, reserved, self use, legal and no management follow the unit page's status chips (v6 handoff).
const unitStatusStyles = {
    inProgress: { backgroundColor: colorPalette.blue200, color: colorPalette.textMain },
    handover: { backgroundColor: colorPalette.blue500, color: colorPalette.white },
    vacant: { backgroundColor: colorPalette.blue400, color: colorPalette.textMain },
    rented: { backgroundColor: "#136956", color: colorPalette.white },
    completed: { backgroundColor: colorPalette.blue100, color: colorPalette.textDark },
    selfInterest: { backgroundColor: colorPalette.blue100, color: colorPalette.textDark },
    canceled: { backgroundColor: colorPalette.errorLight, color: colorPalette.errorDark },
    terminated: { backgroundColor: colorPalette.blue100, color: colorPalette.textDark },
    reserved: { backgroundColor: colorPalette.successLight, color: "#136956" },
    legal: { backgroundColor: "#761414", color: colorPalette.white },
    noManagement: { backgroundColor: colorPalette.blue600, color: colorPalette.white },
};

// Define styles for package tags
const packageStyles = {
    default: { backgroundColor: colorPalette.blue300, color: colorPalette.textMain },
    lite: { backgroundColor: colorPalette.blue300, color: colorPalette.textMain },
    basic: { backgroundColor: "linear-gradient(83.03deg, #666B80 -17.63%, #7FCCFA 99.63%)", color: colorPalette.white },
    premium: { backgroundColor: "linear-gradient(85.51deg, #936E13 6.81%, #DDBA66 98.53%)", color: colorPalette.white },
    premiumSpecial: { backgroundColor: "linear-gradient(85.51deg, #936E13 6.81%, #DDBA66 98.53%)", color: colorPalette.white },
    noManagement: { backgroundColor: colorPalette.blue600, color: colorPalette.white },
};

const Tag: React.FC<TagProps> = (props) => {
    const isMobile = useIsMobile();

    let styles;

    // Handle unit status tags
    if (props.type === 'unitStatus') {
        styles = unitStatusStyles[props.variant];
    }
    // Handle package tags
    else if (props.type === 'package') {
        styles = packageStyles[props.variant];
    }

    const backgroundColor = props.backgroundColor || styles?.backgroundColor || '#FFFFFF';
    const color = props.color || styles?.color || '#000000';

    return (
        <TagContainer
            backgroundColor={backgroundColor}
            color={color}
            size={props.size ?? "L"}
        >
            {/* Optional logo */}
            {props.type === "package" && <TagLogo src={props.variant === "default" ? LogoIconDark : LogoIcon} alt="logo" style={{ marginRight: props.size === "XS" || props.size === "M" ? "0px" : "8px" }} />}

            {/* Icon before label */}
            {props.icon && <MaterialIcon icon={props.icon} size={24} marginRight={props.size === "XS" || props.size === "M" ? "0px" : "8px"} />}

            {props.type === "unitStatus" && (
                <>
                    {props.label}
                </>
            )}

            {!isMobile && props.type === "package" && props.size !== "XS" && props.size !== "M" && (
                <>
                    {props.variant === "default" ? "Standard" : props.variant === "lite" ? "Lite" : props.variant === "basic" ? "Basic" : props.variant === "premium" ? "Premium" : props.variant === "premiumSpecial" ? "Special" : "Kein Management"}
                </>
            )}

            {/* Optional tooltip */}
            {props.tooltip && (
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{props.tooltip}</Tooltip>}
                >
                    <TagTooltipIcon>
                        <MaterialIcon icon="info" size={24} />
                    </TagTooltipIcon>
                </OverlayTrigger>
            )}
        </TagContainer>
    );
};

export default Tag;
